/**
 * Contains methods to help with view transition animations in neo components.
 * View transition animations work by adding a new item to a container, then animating both the old child and new child at the same time to transition from one view to another.
 */
var AnimationHelper = /** @class */ (function () {
    /**
     * Creates an animation helper
     * @param container The container element which will host the old and new elements.
     * @param animationClassName The css class which contains the transition style.
     */
    function AnimationHelper(container, animationClassName) {
        this.container = container;
        this.animationClassName = animationClassName;
        this.zIndex = 0;
    }
    /**
     * Adds a new child to the container.
     * @param resizeContainer If true, resizes the container to be the same size as the child.
     */
    AnimationHelper.prototype.addChild = function (child, resizeContainer) {
        child.style.position = "absolute";
        child.style.zIndex = this.zIndex++;
        if (resizeContainer) {
            var computedStyle = window.getComputedStyle(child);
            this.container.style.width = (child.offsetWidth + parseFloat(computedStyle.marginLeft || "0") + parseFloat(computedStyle.marginRight || "0")) + "px";
            this.container.style.height = (child.offsetHeight + parseFloat(computedStyle.marginTop || "0") + parseFloat(computedStyle.marginBottom || "0")) + "px";
        }
        this.previousChild = this.currentChild;
        this.currentChild = child;
    };
    /**
     * Marks a child element of the container for removal. The element will only be removed from the DOM when the animation is complete.
     */
    AnimationHelper.beginRemove = function (container, element) {
        // TODO: check that the element has a transision of the correct type applied to it, otherwise remove immediately.
        element.addEventListener("transitionend", function () {
            if (container.contains(element)) {
                container.removeChild(element);
            }
        });
    };
    /**
     * Removes the previous child immediately with no animation.
     */
    AnimationHelper.prototype.replace = function () {
        if (this.previousChild) {
            this.container.removeChild(this.previousChild);
        }
    };
    /**
     * Moves the old and new child in the specified direction simultaniously.
     * @param delta Positive value for right / down.
     */
    AnimationHelper.prototype.slide = function (delta, horizontal) {
        if (this.previousChild) {
            AnimationHelper.beginSlide(this.previousChild, this.animationClassName, horizontal, 0, this.container.offsetWidth * -delta);
            AnimationHelper.beginRemove(this.container, this.previousChild);
        }
        AnimationHelper.beginSlide(this.currentChild, this.animationClassName, horizontal, this.container.offsetWidth * delta, 0);
    };
    /**
     * Fades both the old and new child simultaniously.
     */
    AnimationHelper.prototype.fade = function () {
        if (this.previousChild) {
            AnimationHelper.fade(this.previousChild, this.animationClassName, false);
            AnimationHelper.beginRemove(this.container, this.previousChild);
        }
        AnimationHelper.fade(this.currentChild, this.animationClassName, true);
    };
    /**
     * Fades out the old child and scales the new child.
     * @param fromAbove True if the new child must scale down to 1. Otherwise scales up to 1.
     */
    AnimationHelper.prototype.fadeAndScale = function (fromAbove) {
        if (this.previousChild) {
            AnimationHelper.fade(this.previousChild, this.animationClassName, false);
            AnimationHelper.beginRemove(this.container, this.previousChild);
        }
        AnimationHelper.scale(this.currentChild, this.animationClassName, fromAbove ? 1.5 : 0.2, 1);
    };
    AnimationHelper.beginSlide = function (element, animationClassName, horizontal, start, end) {
        var direction = horizontal ? "translateX(" : "translateY(";
        AnimationHelper.applyTransition(element, animationClassName, "transform", direction + start + "px)", direction + end + "px)");
    };
    AnimationHelper.fade = function (element, animationClassName, fadeIn) {
        AnimationHelper.applyTransition(element, animationClassName, "opacity", fadeIn ? "0" : "1", fadeIn ? "1" : "0");
    };
    AnimationHelper.scale = function (element, animationClassName, scaleStart, scaleEnd) {
        AnimationHelper.applyTransition(element, animationClassName, "transform", "scale(" + scaleStart + ")", "scale(" + scaleEnd + ")");
    };
    /**
     * Applies a transition on a certain property as well as setting the start and end values. Ensures the start value is set before the transition class is applied.
     */
    AnimationHelper.applyTransition = function (element, className, styleProperty, startValue, endValue) {
        element.classList.remove(className);
        element.style.setProperty(styleProperty, startValue);
        AnimationHelper.triggerReflow(element);
        element.classList.add(className);
        AnimationHelper.triggerReflow(element);
        element.style.setProperty(styleProperty, endValue);
    };
    AnimationHelper.triggerReflow = function (element) {
        void element.offsetLeft;
    };
    return AnimationHelper;
}());
export default AnimationHelper;
var CollapseAnimationHelper = /** @class */ (function () {
    function CollapseAnimationHelper(element, shouldShow, state, onHidden) {
        var _a;
        this.element = element;
        this.shouldShow = shouldShow;
        this.state = state;
        this.onHidden = onHidden;
        this.hasTransitioned = false;
        this.afterTransitionedDelegate = this.afterTransitioned.bind(this);
        element.addEventListener("transitionend", this.afterTransitionedDelegate);
        this.afterTransitioned();
        this.defaultOverflow = (_a = window.getComputedStyle(element).overflowY) !== null && _a !== void 0 ? _a : "visible";
    }
    /** Restores the element to its normal height. */
    CollapseAnimationHelper.prototype.restore = function (force) {
        if (force === void 0) { force = false; }
        if (!this.shouldShow || force) {
            this.shouldShow = true;
            this.hasTransitioned = false;
            // Remove animation and collapsed classes, set the height equal to the calculated height, then collapse again while animation is off.
            this.element.style.display = "";
            this.element.style.transition = "none";
            this.element.classList.remove("collapsed");
            this.element.style.height = this.element.scrollHeight + "px";
            this.element.style.overflowY = "hidden";
            this.element.classList.add("collapsed");
            // Add animation back, and remove collapsed class to bring alert to specified height.
            AnimationHelper.triggerReflow(this.element);
            this.element.style.transition = "";
            this.element.classList.remove("collapsed");
        }
    };
    /** Collapses the element, and then hides it. */
    CollapseAnimationHelper.prototype.collapse = function (force) {
        if (force === void 0) { force = false; }
        if (this.shouldShow || force) {
            this.shouldShow = false;
            this.hasTransitioned = false;
            this.element.style.height = this.element.offsetHeight + "px";
            this.element.style.overflowY = "hidden";
            AnimationHelper.triggerReflow(this.element);
            this.element.classList.add("collapsed");
        }
    };
    CollapseAnimationHelper.prototype.showHide = function (shouldShow, force) {
        if (force === void 0) { force = false; }
        if (shouldShow) {
            this.restore(force);
        }
        else {
            this.collapse(force);
        }
    };
    CollapseAnimationHelper.prototype.afterTransitioned = function () {
        if (!this.hasTransitioned) {
            this.hasTransitioned = true;
            if (this.shouldShow) {
                this.element.style.height = "auto";
                this.element.style.overflowY = this.defaultOverflow;
            }
            else {
                this.element.style.display = "none";
                if (this.onHidden) {
                    this.onHidden(this.state);
                }
            }
        }
    };
    CollapseAnimationHelper.prototype.destroy = function () {
        this.element.removeEventListener("transitionend", this.afterTransitionedDelegate);
    };
    return CollapseAnimationHelper;
}());
export { CollapseAnimationHelper };
